/**
 * Family Context
 * Manages family state and subscription information
 */

import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { useAuth } from './AuthContext';
import { familyAPI, User, Family, SubscriptionPlan, Subscription } from '../services/api';

interface FamilyContextType {
  // State
  family: Family | null;
  subscription: Subscription | null;
  plans: SubscriptionPlan[];
  pendingInvitations: Array<{
    id: string;
    email: string;
    anticipated_user_type: 'guardian' | 'athlete';
    expires_at: string;
    message?: string;
    created_at: string;
  }>;
  loading: boolean;
  error: string | null;

  // Actions
  createFamily: (name: string) => Promise<void>;
  updateFamily: (updates: { name?: string }) => Promise<void>;
  loadFamily: () => Promise<void>;
  loadPlans: () => Promise<void>;
  loadSubscription: () => Promise<void>;
  loadPendingInvitations: () => Promise<void>;
  inviteMember: (email: string, userType?: 'guardian' | 'athlete' | null) => Promise<void>;
  resendInvitation: (invitationId: string) => Promise<void>;
  cancelInvitation: (invitationId: string) => Promise<void>;
  removeMember: (userId: string) => Promise<void>;
  createSubscription: (planId: string, paymentMethodId: string | null) => Promise<void>;
  clearError: () => void;
  resetState: () => void;
}

const FamilyContext = createContext<FamilyContextType | undefined>(undefined);

export const useFamily = (): FamilyContextType => {
  const context = useContext(FamilyContext);
  if (!context) {
    throw new Error('useFamily must be used within a FamilyProvider');
  }
  return context;
};

interface FamilyProviderProps {
  children: ReactNode;
}

export const FamilyProvider: React.FC<FamilyProviderProps> = ({ children }) => {
  const { user, sessionToken, registerLogoutCallback } = useAuth();
  const [family, setFamily] = useState<Family | null>(null);
  const [subscription, setSubscription] = useState<Subscription | null>(null);
  const [plans, setPlans] = useState<SubscriptionPlan[]>([]);
  const [pendingInvitations, setPendingInvitations] = useState<Array<{
    id: string;
    email: string;
    anticipated_user_type: 'guardian' | 'athlete';
    expires_at: string;
    message?: string;
    created_at: string;
  }>>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const clearError = () => setError(null);

  const resetState = () => {
    setFamily(null);
    setSubscription(null);
    setPendingInvitations([]);
    setPlans([]);
    setLoading(false);
    setError(null);
  };

  const createFamily = async (name: string) => {
    if (!sessionToken) throw new Error('Not authenticated');
    
    try {
      setLoading(true);
      setError(null);
      
      const response = await familyAPI.createFamily(sessionToken, name);
      setFamily(response.family);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create family');
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const updateFamily = async (updates: { name?: string }) => {
    if (!sessionToken) throw new Error('Not authenticated');
    
    try {
      setLoading(true);
      setError(null);
      
      const response = await familyAPI.updateFamily(sessionToken, updates);
      setFamily(response.family);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update family');
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const loadFamily = async () => {
    if (!sessionToken) return;

    try {
      setLoading(true);
      setError(null);
      
      const response = await familyAPI.getMyFamily(sessionToken);
      setFamily(response.family);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load family');
    } finally {
      setLoading(false);
    }
  };

  const loadPlans = async () => {
    try {
      setLoading(true);
      setError(null);
      
      // Plans are now managed through RevenueCat
      // These are display-only - actual purchase uses RevenueCat SDK packages
      // Single paid tier (KGH-785 pricing decision): Premium retired to à-la-carte,
      // Regular repriced to $19.95 (per the Aug-2026 flyer). The product id stays
      // aps_regular_3999 — it's the immutable store identifier; the actual charged
      // price lives in App Store / Play / RevenueCat and is repriced there, not here.
      // (Existing-subscriber migration to $19.95 eff. 9/1 is a billing-side task.)
      const revenueCatPlans: SubscriptionPlan[] = [
        {
          id: 'aps_regular_3999',
          name: 'APS Membership',
          description: 'Full access to recruiting tools and athlete profiles',
          prices: [{
            id: 'aps_regular_3999',
            amount: 1995,
            currency: 'usd',
            interval: 'month',
            interval_count: 1
          }],
          features: [
            'Full access to recruiting tools',
            'Athlete profile management',
            'School search and matching',
            'Email templates'
          ]
        }
      ];
      setPlans(revenueCatPlans);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Unknown error';
      setError(`Failed to load plans: ${errorMessage}`);
    } finally {
      setLoading(false);
    }
  };

  const loadSubscription = async () => {
    if (!sessionToken) return;
    
    try {
      setLoading(true);
      setError(null);
      
      // Subscription status is now loaded from RevenueCat via the backend
      // This is a legacy function - components should use revenueCatService directly
      // Keeping for backward compatibility but returning null
      setSubscription(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load subscription');
    } finally {
      setLoading(false);
    }
  };

  const loadPendingInvitations = async () => {
    if (!sessionToken) {
            return;
    }
    
    try {
            setLoading(true);
      setError(null);
      
      const response = await familyAPI.getPendingInvitations(sessionToken);
            
      // The response has { invitations: [...], summary: {...} }
      const invitations = response.invitations || [];
                  
      // Filter to show only pending invitations, or show all based on needs
      const pendingOnly = invitations.filter((inv: any) => inv.status === 'pending');
            
      setPendingInvitations(invitations); // Store all invitations
    } catch (err) {
      console.error('❌ Error loading invitations:', err);
      setError(err instanceof Error ? err.message : 'Failed to load pending invitations');
    } finally {
      setLoading(false);
    }
  };

  const inviteMember = async (email: string, userType?: 'guardian' | 'athlete' | null) => {
    if (!sessionToken) throw new Error('Not authenticated');

    try {
      setLoading(true);
      setError(null);

      await familyAPI.inviteMember(sessionToken, email, userType ?? null);
      // Don't reload family here as it can trigger navigation side effects
      // The invitation list will be reloaded separately if needed
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to send invitation');
      throw err;
    } finally {
      setLoading(false);
    }
  };

  // Resend / cancel / remove — pair with the new backend routes added in
  // the invite-redesign pass. Errors bubble up so caller can react (e.g.
  // free-tier paywall trigger on FREE_TIER_INVITE_LIMIT).
  const resendInvitation = async (invitationId: string) => {
    if (!sessionToken) throw new Error('Not authenticated');
    try {
      setLoading(true);
      setError(null);
      await familyAPI.resendInvitation(sessionToken, invitationId);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to resend invitation');
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const cancelInvitation = async (invitationId: string) => {
    if (!sessionToken) throw new Error('Not authenticated');
    try {
      setLoading(true);
      setError(null);
      await familyAPI.cancelInvitation(sessionToken, invitationId);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to cancel invitation');
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const removeMember = async (userId: string) => {
    if (!sessionToken) throw new Error('Not authenticated');
    try {
      setLoading(true);
      setError(null);
      await familyAPI.removeMember(sessionToken, userId);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to remove family member');
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const createSubscription = async (planId: string, paymentMethodId: string | null) => {
    // Subscriptions are now created through RevenueCat Web SDK
    // Use revenueCatService.purchasePackage() instead
    throw new Error('Use RevenueCat purchasePackage() for subscriptions. See revenueCatService.ts');
  };

  // Load family data when user changes
  useEffect(() => {
    if (user?.family_id && sessionToken) {
      loadFamily();
      loadSubscription();
    }
  }, [user?.family_id, sessionToken]);

  // Register resetState callback with AuthContext for logout clearing
  useEffect(() => {
    if (registerLogoutCallback) {
      registerLogoutCallback(resetState);
    }
  }, [registerLogoutCallback]);

  const value: FamilyContextType = {
    // State
    family,
    subscription,
    plans,
    pendingInvitations,
    loading,
    error,

    // Actions
    createFamily,
    updateFamily,
    loadFamily,
    loadPlans,
    loadSubscription,
    loadPendingInvitations,
    inviteMember,
    resendInvitation,
    cancelInvitation,
    removeMember,
    createSubscription,
    clearError,
    resetState,
  };

  return (
    <FamilyContext.Provider value={value}>
      {children}
    </FamilyContext.Provider>
  );
};
